const BADGE_ATTRIBUTE = "clientDetailBadge";

/**
 * Field option keys come back quoted (`"lit_attorney"`) from the non-flat
 * field_options serializer, while contact.relationType is always bare. Strip the
 * stray quotes off both sides before matching.
 */
const normalizeKey = (key) => (typeof key === "string" ? key.replace(/^"+|"+$/g, "") : key);

/**
 * How many contact types have a badge configured — the most badges the header can
 * ever show, and therefore the point at which paging through contacts can stop early.
 */
export const countBadgeConfiguredTypes = (contactTypeOptions) =>
  (Array.isArray(contactTypeOptions) ? contactTypeOptions : []).filter(
    (option) => option?.additionalAttributes?.[BADGE_ATTRIBUTE]?.text
  ).length;

/**
 * Resolve the case's contacts down to one badge per configured contact type.
 *
 * Badge text and color are admin-configured per contact type via the field option's
 * `additional_attributes.clientDetailBadge`. A contact type without that attribute
 * produces no badge, so the header is unchanged until someone configures it.
 */
export const buildContactTypeBadges = (contacts, contactTypeOptions) => {
  const optionsByKey = new Map(
    (Array.isArray(contactTypeOptions) ? contactTypeOptions : []).map((option) => [
      normalizeKey(option?.key),
      option,
    ])
  );
  const badgesByKey = new Map();

  (Array.isArray(contacts) ? contacts : []).forEach((contact) => {
    const key = normalizeKey(contact?.relationType);
    if (!key || badgesByKey.has(key)) {
      return;
    }

    const option = optionsByKey.get(key);
    const badge = option?.additionalAttributes?.[BADGE_ATTRIBUTE];
    if (!badge?.text) {
      return;
    }

    badgesByKey.set(key, {
      key,
      text: badge.text,
      color: badge.color,
      order: option?.order ?? 0,
    });
  });

  return [...badgesByKey.values()].sort((a, b) => a.order - b.order);
};

/**
 * Contact-type key -> dot color, for every type that has one configured.
 *
 * Lets the Contacts tab show the same dot the header does. Unlike the header badge this
 * needs no `text`: the row already prints the type's own label next to the dot.
 */
export const buildContactTypeColors = (contactTypeOptions) => {
  const colors = {};

  (Array.isArray(contactTypeOptions) ? contactTypeOptions : []).forEach((option) => {
    const key = normalizeKey(option?.key);
    const color = option?.additionalAttributes?.[BADGE_ATTRIBUTE]?.color;
    if (key && color) {
      colors[key] = color;
    }
  });

  return colors;
};

/**
 * Look up a contact type's dot color, tolerating the quoted keys the unflattened
 * field_options serializer can return.
 */
export const contactTypeColorFor = (contactTypeColors, relationTypeKey) =>
  (contactTypeColors || {})[normalizeKey(relationTypeKey)];

export default buildContactTypeBadges;
